'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterOption {
  label: string;
  value: string;
}

interface FilterPanelProps {
  activeFilters: {
    category: string;
    complexity: string;
    roiTimeline: string;
    businessFunction: string;
  };
  onFilterChange: (filterType: string, value: string) => void;
  onClearFilters: () => void;
}

const FilterPanel = ({ activeFilters, onFilterChange, onClearFilters }: FilterPanelProps) => {
  const categories: FilterOption[] = [
    { label: 'Alle categorieën', value: 'all' },
    { label: 'Automatisering', value: 'automation' },
    { label: 'Klantenservice', value: 'customer-service' },
    { label: 'Marketing', value: 'marketing' },
    { label: 'Data & Analytics', value: 'data-analytics' },
  ];

  const complexities: FilterOption[] = [
    { label: 'Alle niveaus', value: 'all' },
    { label: 'Laag', value: 'low' },
    { label: 'Gemiddeld', value: 'medium' },
    { label: 'Hoog', value: 'high' },
  ];

  const roiTimelines: FilterOption[] = [
    { label: 'Alle tijdlijnen', value: 'all' },
    { label: '1-3 maanden', value: '1-3' },
    { label: '3-6 maanden', value: '3-6' },
    { label: '6-12 maanden', value: '6-12' },
  ];

  const businessFunctions: FilterOption[] = [
    { label: 'Alle functies', value: 'all' },
    { label: 'Verkoop', value: 'sales' },
    { label: 'Marketing', value: 'marketing' },
    { label: 'Operaties', value: 'operations' },
    { label: 'Klantenservice', value: 'customer-service' },
  ];

  const hasActiveFilters = 
    activeFilters.category !== 'all' ||
    activeFilters.complexity !== 'all' ||
    activeFilters.roiTimeline !== 'all' ||
    activeFilters.businessFunction !== 'all';

  return (
    <div className="glassmorphic rounded-2xl p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Icon name="FunnelIcon" size={24} className="text-primary" />
          <h2 className="text-xl font-display font-bold text-foreground">Filters</h2>
        </div>
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-semibold text-muted-foreground hover:text-primary hover:bg-muted transition-all duration-300"
          >
            <Icon name="XMarkIcon" size={16} />
            <span>Wis filters</span>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-semibold text-muted-foreground mb-2">
            Categorie
          </label>
          <select
            value={activeFilters.category}
            onChange={(e) => onFilterChange('category', e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg bg-input border border-border text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300 outline-none"
          >
            {categories.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-muted-foreground mb-2">
            Complexiteit
          </label>
          <select
            value={activeFilters.complexity}
            onChange={(e) => onFilterChange('complexity', e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg bg-input border border-border text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300 outline-none"
          >
            {complexities.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-muted-foreground mb-2">
            ROI Timeline
          </label>
          <select
            value={activeFilters.roiTimeline}
            onChange={(e) => onFilterChange('roiTimeline', e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg bg-input border border-border text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300 outline-none"
          >
            {roiTimelines.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-muted-foreground mb-2">
            Bedrijfsfunctie
          </label>
          <select
            value={activeFilters.businessFunction}
            onChange={(e) => onFilterChange('businessFunction', e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg bg-input border border-border text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300 outline-none"
          >
            {businessFunctions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;